import React, { useState, useEffect, useRef } from "react";
import { scenariosAPI } from "../../services/api";
import {
  Plus,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
  BrainCircuit,
  Code2,
  ClipboardList,
} from "lucide-react";
import CodeMetricEditor from "./CodeMetricEditor";
import MultiSelect from "../ui/multi-select";

export const CODE_TEMPLATE = `# ── Your evaluation logic goes here ──
# Modify the condition below to match your metric's criteria.

transcript = context["transcript"]

# TODO: Replace this with your evaluation logic
# Examples:
#   Check silence: context["silence"].get("silences", [])
#   Check latency: context["latency"].get("avg_ms", 0)
#   Check transcript: [t["content"] for t in transcript if t["role"] == "assistant"]
#   Check other metrics: context["metrics_results"].get("Metric Name", {})

condition_met = True  # ← change this to your actual check

# ── Metric result (required) ──
metric["result"] = condition_met                              # ← True/False, 1-5, enum value, or number
metric["explanation"] = "Condition was met" if condition_met else "Condition was not met"

# ── Structured output (optional) ──
structured_output["name"] = "my_check"                        # ← rename to describe your check
structured_output["value"] = "pass" if condition_met else "fail"
structured_output["classification"] = "meets_expectations" if condition_met else "requires_attention"
`;

/**
 * Shared metric definition form. Owns all field state + payload building and
 * calls onSubmit(payload, meta). Persistence/navigation/toasts live in the
 * parent (the app's CustomMetrics page or the Admin gallery page).
 *
 * Props:
 *   mode             "create" | "edit"
 *   initialMetric    metric object to prefill (edit), or null
 *   showAssignment   show the agent/scenario/global section (app=true, gallery=false)
 *   canWrite         enable the submit button
 *   agents           [] (only used when showAssignment)
 *   clientId         workspace id (scenarios fetch + code editor)
 *   defaultAgentId   initial agent selection (create)
 *   submitLabel      submit button text
 *   onSubmit         (payload, meta) => Promise   meta = { isSystemMetric, agentId, llmInstructions, isGlobal }
 *   onCancel         () => void
 *   onResetSystemPrompt  optional () => void — renders the system-metric reset banner when provided
 *   notify           (message, type) => void — for inline validation messages
 *   readOnly         render every field disabled and hide the action buttons (view-only)
 */
const MetricForm = ({
  mode = "create",
  initialMetric = null,
  showAssignment = true,
  canWrite = true,
  agents = [],
  clientId,
  defaultAgentId = "",
  submitLabel,
  onSubmit,
  onCancel,
  onResetSystemPrompt,
  notify = () => {},
  readOnly = false,
}) => {
  const isEditMode = mode === "edit";

  const [metricCategory, setMetricCategory] = useState("llm_as_judge");
  const [metricName, setMetricName] = useState("");
  const [metricType, setMetricType] = useState("");
  const [resultType, setResultType] = useState("boolean");
  const [analysisType, setAnalysisType] = useState("both");
  const [enumValues, setEnumValues] = useState([]);
  const [enumValueInput, setEnumValueInput] = useState("");
  const [llmInstructions, setLlmInstructions] = useState("");
  const [codeSnippet, setCodeSnippet] = useState("");
  const [allowVariations, setAllowVariations] = useState(false);
  const [allowedValueInput, setAllowedValueInput] = useState("");
  const [evaluationTrigger, setEvaluationTrigger] = useState({
    type: "",
    state_prompt: "",
  });
  const [isCritical, setIsCritical] = useState(false);
  const [evalObservability, setEvalObservability] = useState(true);
  const [evalSimulation, setEvalSimulation] = useState(true);
  const [isGlobal, setIsGlobal] = useState(false);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [specificTextInput, setSpecificTextInput] = useState("");
  const [showStructuredOutput, setShowStructuredOutput] = useState(false);
  const [structured_output, setStructuredOutput] = useState([
    {
      condition: "",
      set_metrics_name: "",
      as_metrics_expected_value: "",
      outcome_classification: "",
    },
  ]);
  const [metrics_type_config, setMetricsTypeConfig] = useState({
    text: "",
    allow_fuzzy: false,
    allowed_values: [],
    condition_match: "",
  });
  const [scenarios, setScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);
  const [isSystemMetric, setIsSystemMetric] = useState(false);
  const [metricAgentId, setMetricAgentId] = useState(defaultAgentId || "");
  const hasLoadedFromServer = useRef(false);

  const matchingConditions = [
    "At least one value from the list must be present",
    "Exactly one value from the list must be present",
    "None of the values from the list should be present",
  ];

  // Prefill from an existing metric (edit / clone)
  useEffect(() => {
    if (!initialMetric) return;
    const m = initialMetric;
    hasLoadedFromServer.current = true;
    setMetricName(m.name || "");
    setIsSystemMetric(m.metrics_type === "system_llm" || m.is_custom === false);
    setMetricType(
      m.metrics_type === "system_llm"
        ? "custom_evaluation"
        : m.metrics_type || "",
    );
    setResultType(m.result_type || "boolean");
    setAnalysisType(m.analysis_type === "text" ? "text" : "both");
    setEnumValues(m.result_type_config?.values || []);
    setLlmInstructions(m.llm_instructions || "");
    setCodeSnippet(m.code_snippet || "");
    if (m.metrics_type === "code_as_judge") setMetricCategory("code_as_judge");
    setIsCritical(m.is_critical || false);
    setEvalObservability(m.eval_observability !== false);
    setEvalSimulation(m.eval_simulation !== false);
    setIsGlobal(m.mark_as_global || m.scope === "global" || false);
    setEvaluationTrigger(m.evaluation_trigger || { type: "", state_prompt: "" });
    setStructuredOutput(m.structured_output || []);
    setMetricsTypeConfig(m.metrics_type_config || {});
    setSelectedScenarios(m.scenario_ids || m.scenarios?.map((s) => s.id) || []);
    const agentId = m.agent_ids?.[0] || m.agents?.[0]?.id;
    if (agentId) setMetricAgentId(agentId);
    setSpecificTextInput(m.metrics_type_config?.text || "");
    setAllowVariations(m.metrics_type_config?.allow_fuzzy || false);
    if (
      m.structured_output?.length > 0 &&
      m.structured_output.some((l) => l.set_metrics_name)
    ) {
      setShowStructuredOutput(true);
    }
  }, [initialMetric]);

  // Default agent from context when creating
  useEffect(() => {
    if (!isEditMode && defaultAgentId && !metricAgentId) {
      setMetricAgentId(defaultAgentId);
    }
  }, [defaultAgentId, isEditMode, metricAgentId]);

  // Load scenarios filtered by selected agent (assignment mode only)
  useEffect(() => {
    if (!showAssignment) {
      setLoadingScenarios(false);
      return;
    }
    const fetchScenarios = async () => {
      if (!clientId) return;
      setLoadingScenarios(true);
      try {
        if (metricAgentId) {
          const data = await scenariosAPI.getScenariosByAgent(
            metricAgentId,
            clientId,
          );
          setScenarios(Array.isArray(data) ? data : data?.data || []);
        } else {
          const response = await scenariosAPI.getScenarios(clientId);
          setScenarios(response?.data?.data || []);
        }
      } catch (error) {
        console.error("Error fetching scenarios:", error);
        setScenarios([]);
      } finally {
        setLoadingScenarios(false);
      }
    };
    fetchScenarios();
    if (!isEditMode) setSelectedScenarios([]);
  }, [metricAgentId, clientId, showAssignment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset evaluation method when switching metric category
  useEffect(() => {
    if (metricCategory === "code_as_judge") {
      setMetricType("");
      setSpecificTextInput("");
      setAllowVariations(false);
      setMetricsTypeConfig({});
      if (!isEditMode && !codeSnippet) {
        setCodeSnippet(CODE_TEMPLATE);
      }
    }
  }, [metricCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasLoadedFromServer.current) {
      hasLoadedFromServer.current = false;
      return;
    }
    if (metricType === "specific_text_/_phrase_check") {
      if (resultType === "enum") {
        setResultType("boolean");
        setEnumValues([]);
      }
      setStructuredOutput([
        {
          condition: "value is present",
          set_metrics_name: "",
          as_metrics_expected_value: "",
          outcome_classification: "",
        },
        {
          condition: "value is not present",
          set_metrics_name: "",
          as_metrics_expected_value: "",
          outcome_classification: "",
        },
      ]);
    } else if (metricType === "permitted_options_check") {
      setStructuredOutput([
        {
          condition: "condition is met",
          set_metrics_name: "",
          as_metrics_expected_value: "",
          outcome_classification: "",
        },
        {
          condition: "condition is not met",
          set_metrics_name: "",
          as_metrics_expected_value: "",
          outcome_classification: "",
        },
      ]);
    } else {
      setStructuredOutput([
        {
          condition: "",
          set_metrics_name: "",
          as_metrics_expected_value: "",
          outcome_classification: "",
        },
      ]);
    }
  }, [metricType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (metricType === "specific_text_/_phrase_check") {
      setMetricsTypeConfig({
        text: specificTextInput,
        allow_fuzzy: allowVariations,
      });
    } else if (metricType === "permitted_options_check") {
      setMetricsTypeConfig((prev) => ({
        allowed_values: prev.allowed_values || [],
        condition_match: prev.condition_match || matchingConditions[0],
      }));
    } else if (metricType === "custom_evaluation") {
      setMetricsTypeConfig({});
    }
  }, [metricType, specificTextInput, allowVariations]); // eslint-disable-line react-hooks/exhaustive-deps

  // In read-only mode the collapse toggle is disabled, so keep the
  // Structured Output section expanded to make its content visible.
  useEffect(() => {
    if (readOnly) setShowStructuredOutput(true);
  }, [readOnly]);

  const handleAllowedValueKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && allowedValueInput.trim()) {
      e.preventDefault();
      const newValues = [
        ...(metrics_type_config.allowed_values || []),
        allowedValueInput.trim(),
      ];
      setMetricsTypeConfig((prev) => ({ ...prev, allowed_values: newValues }));
      setAllowedValueInput("");
    }
  };

  const handleTriggerTypeChange = (type) =>
    setEvaluationTrigger({ type, state_prompt: "" });
  const handleStatePromptChange = (prompt) =>
    setEvaluationTrigger((prev) => ({ ...prev, state_prompt: prompt }));

  const handleEnumValueKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && enumValueInput.trim()) {
      e.preventDefault();
      if (!enumValues.includes(enumValueInput.trim())) {
        setEnumValues([...enumValues, enumValueInput.trim()]);
      }
      setEnumValueInput("");
    }
  };

  const handleLogicChange = (index, field, value) => {
    const newLogic = [...structured_output];
    newLogic[index] = { ...newLogic[index], [field]: value };
    setStructuredOutput(newLogic);
  };

  const addLogicRow = () => {
    setStructuredOutput([
      ...structured_output,
      {
        condition: "",
        set_metrics_name: "",
        as_metrics_expected_value: "",
        outcome_classification: "",
      },
    ]);
  };

  const handleSpecificTextChange = (value) => {
    setSpecificTextInput(value);
    setMetricsTypeConfig((prev) => ({ ...prev, text: value }));
  };

  const handleAllowVariationsChange = (value) => {
    setAllowVariations(value);
    setMetricsTypeConfig((prev) => ({ ...prev, allow_fuzzy: value }));
  };

  const handleConditionMatchChange = (value) =>
    setMetricsTypeConfig((prev) => ({ ...prev, condition_match: value }));

  const availableVariables = [
    { token: "{provided_transcript}", label: "Provided Transcript", always: true },
    {
      token: "{generated_transcript}",
      label: "Generated Transcript",
      always: false,
      hint: "Voice calls only",
    },
    { token: "{call_duration}", label: "Call Duration", always: true },
    { token: "{call_metadata}", label: "Call Metadata", always: true },
    { token: "{call_direction}", label: "Call Direction", always: true },
    { token: "{agent_name}", label: "Agent Name", always: true },
  ];

  const insertVariable = (token) =>
    setLlmInstructions((prev) => prev + " " + token);

  const handleSubmit = async () => {
    if (showAssignment && !isGlobal && !metricAgentId) {
      notify("Please select an agent or mark the metric as global", "error");
      return;
    }
    const evaluation_trigger = {
      type: evaluationTrigger.type,
      ...(evaluationTrigger.type === "call_state" && {
        state_prompt: evaluationTrigger.state_prompt,
      }),
    };
    const payload = {
      name: metricName,
      metrics_type:
        metricCategory === "code_as_judge" ? "code_as_judge" : metricType,
      result_type: resultType,
      analysis_type: analysisType,
      result_type_config:
        resultType === "enum"
          ? {
              values:
                metricType === "permitted_options_check"
                  ? metrics_type_config.allowed_values || []
                  : enumValues,
            }
          : {},
      llm_instructions:
        metricCategory === "code_as_judge" ? "" : llmInstructions,
      code_snippet: metricCategory === "code_as_judge" ? codeSnippet : null,
      metrics_type_config:
        metricCategory === "code_as_judge" ? {} : { ...metrics_type_config },
      structured_output: showStructuredOutput ? structured_output : [],
      evaluation_trigger,
      is_critical: resultType === "boolean" ? isCritical : false,
      eval_observability: evalObservability,
      eval_simulation: evalSimulation,
      is_custom: true,
      ...(showAssignment && { mark_as_global: isGlobal, client_id: clientId }),
      ...(showAssignment && !isGlobal && { scenario_ids: selectedScenarios }),
      ...(showAssignment &&
        !isGlobal &&
        metricAgentId && { agent_ids: [parseInt(metricAgentId)] }),
    };
    onSubmit?.(payload, {
      isSystemMetric,
      agentId: metricAgentId,
      llmInstructions,
      isGlobal,
    });
  };

  const renderMetricTypeSpecificFields = () => {
    switch (metricType) {
      case "specific_text_/_phrase_check":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground">
                Text/Phrase
              </label>
              <input
                type="text"
                value={specificTextInput}
                onChange={(e) => handleSpecificTextChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                placeholder="Enter text or phrase to check"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={allowVariations}
                onChange={(e) => handleAllowVariationsChange(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-input rounded"
              />
              <label className="ml-2 block text-sm text-foreground">
                Allow Variations
              </label>
            </div>
          </div>
        );

      case "permitted_options_check":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground">
                Enum Allowed Values
              </label>
              <p className="text-xs text-muted-foreground mb-1">
                These are the only values the LLM can return for this metric.
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {metrics_type_config.allowed_values?.map((val, idx) => (
                  <span
                    key={idx}
                    className="flex items-center bg-muted rounded px-2 py-1 text-xs"
                  >
                    {val}
                    <button
                      type="button"
                      className="ml-1 text-muted-foreground hover:text-red-500"
                      onClick={() => {
                        const newValues =
                          metrics_type_config.allowed_values.filter(
                            (_, i) => i !== idx,
                          );
                        setMetricsTypeConfig((prev) => ({
                          ...prev,
                          allowed_values: newValues,
                        }));
                      }}
                    >
                      &times;
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={allowedValueInput}
                  onChange={(e) => setAllowedValueInput(e.target.value)}
                  onKeyDown={handleAllowedValueKeyDown}
                  className="flex-1 min-w-[120px] rounded border-input bg-background focus:border-primary focus:ring-primary text-sm"
                  placeholder="Type value and press Enter"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground">
                Condition Match
              </label>
              <select
                value={metrics_type_config.condition_match}
                onChange={(e) => handleConditionMatchChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                {matchingConditions.map((condition, index) => (
                  <option key={index} value={condition}>
                    {condition}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case "custom_evaluation":
        if (resultType === "enum") {
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground">
                  Enum Allowed Values
                </label>
                <p className="text-xs text-muted-foreground mb-1">
                  These are the only values the LLM can return for this metric.
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {enumValues.map((val, idx) => (
                    <span
                      key={idx}
                      className="flex items-center bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-3 py-1 text-xs font-medium"
                    >
                      {val}
                      <button
                        type="button"
                        className="ml-1.5 text-indigo-400 hover:text-red-500"
                        onClick={() =>
                          setEnumValues(enumValues.filter((_, i) => i !== idx))
                        }
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={enumValueInput}
                    onChange={(e) => setEnumValueInput(e.target.value)}
                    onKeyDown={handleEnumValueKeyDown}
                    className="flex-1 min-w-[120px] rounded border-input bg-background focus:border-primary focus:ring-primary text-sm"
                    placeholder="Type value and press Enter"
                  />
                </div>
              </div>
            </div>
          );
        }
        return null;

      default:
        return null;
    }
  };

  const renderStructuredOutputSection = () => {
    const isFixedConditions =
      metricType === "specific_text_/_phrase_check" ||
      metricType === "permitted_options_check";

    return (
      <div className="mt-6 border border-border rounded-lg">
        <button
          type="button"
          onClick={() => setShowStructuredOutput(!showStructuredOutput)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/50 rounded-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            {showStructuredOutput ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="text-sm font-semibold text-foreground">
              Structured Output
            </span>
            <span className="text-xs text-muted-foreground">(optional)</span>
          </div>
        </button>

        {showStructuredOutput && (
          <div className="px-4 pb-4">
            <p className="text-muted-foreground text-xs mb-4">
              Break this metric into named structured outputs. Each row defines
              an output field, the value it takes when its condition holds, and
              an optional result classification.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
              <div className="text-xs font-medium text-foreground">
                If this happens
              </div>
              <div className="text-xs font-medium text-foreground">
                Output name
              </div>
              <div className="text-xs font-medium text-foreground">
                Expected value
              </div>
              <div className="text-xs font-medium text-foreground">
                Result Classification
              </div>
            </div>
            {structured_output.map((logic, index) => (
              <div
                key={index}
                className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2 items-center"
              >
                {isFixedConditions ? (
                  <div className="text-sm text-foreground">
                    {logic.condition}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={logic.condition}
                    onChange={(e) =>
                      handleLogicChange(index, "condition", e.target.value)
                    }
                    className="block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    placeholder="Write your condition here"
                  />
                )}
                <input
                  type="text"
                  value={logic.set_metrics_name}
                  onChange={(e) =>
                    handleLogicChange(index, "set_metrics_name", e.target.value)
                  }
                  className="block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  placeholder="identity_verification"
                />
                <input
                  type="text"
                  value={logic.as_metrics_expected_value}
                  onChange={(e) =>
                    handleLogicChange(
                      index,
                      "as_metrics_expected_value",
                      e.target.value,
                    )
                  }
                  className="block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  placeholder="pass"
                />
                <div className="flex items-center gap-2">
                  <select
                    value={logic.outcome_classification || ""}
                    onChange={(e) =>
                      handleLogicChange(
                        index,
                        "outcome_classification",
                        e.target.value,
                      )
                    }
                    className="block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  >
                    <option value="">Select Outcome</option>
                    <option value="meets_expectations">Meets Expectations</option>
                    <option value="exceeds_expectations">
                      Exceeds Expectations
                    </option>
                    <option value="requires_attention">Requires Attention</option>
                    <option value="goal_achieved">Goal Achieved</option>
                    <option value="goal_missed">Goal Missed</option>
                  </select>
                  {structured_output.length > 1 && !isFixedConditions && (
                    <button
                      type="button"
                      onClick={() =>
                        setStructuredOutput(
                          structured_output.filter((_, i) => i !== index),
                        )
                      }
                      className="p-1 text-red-500 hover:text-red-700 focus:outline-none"
                      title="Remove Row"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!isFixedConditions && (
              <button
                type="button"
                onClick={addLogicRow}
                className="mt-2 inline-flex items-center px-3 py-2 border border-input shadow-sm text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Row
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderScenariosDropdown = () => (
    <div className="flex-1">
      <label className="block text-xs font-medium text-foreground mb-2">
        Apply to Scenarios
      </label>
      <MultiSelect
        placeholder="Select scenarios"
        emptyText="No scenarios available"
        loading={loadingScenarios}
        enableSelectAll
        value={selectedScenarios}
        onChange={setSelectedScenarios}
        options={scenarios.map((s) => ({ value: s.id, label: s.name }))}
      />
    </div>
  );

  return (
    <fieldset
      disabled={readOnly}
      className="m-0 min-w-0 space-y-6 border-0 p-0 disabled:opacity-100"
    >
      {isSystemMetric && onResetSystemPrompt && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">This is a system LLM metric.</span>{" "}
            Your edits save as a{" "}
            <span className="font-semibold">per-agent prompt override</span> for
            the selected agent only.
          </p>
          <button
            type="button"
            disabled={!canWrite}
            onClick={onResetSystemPrompt}
            className="shrink-0 text-sm font-medium text-blue-700 hover:text-blue-900 underline disabled:opacity-50"
          >
            Reset to default
          </button>
        </div>
      )}

      {isEditMode && isGlobal && !isSystemMetric && showAssignment && (
        <div className="flex items-start gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg">
          <span className="text-orange-500 mt-0.5 shrink-0">&#9888;</span>
          <p className="text-sm text-orange-800">
            <span className="font-semibold">This is a global metric.</span> Any
            changes affect <span className="font-semibold">all agents</span> in
            your workspace that use it.
          </p>
        </div>
      )}

      {/* Metric Category */}
      <div className="flex gap-2 mb-1">
        {[
          { value: "llm_as_judge", label: "LLM as Judge", icon: BrainCircuit },
          { value: "code_as_judge", label: "Code as Judge", icon: Code2 },
          {
            value: "rubric",
            label: "Rubric",
            icon: ClipboardList,
            disabled: true,
          },
        ].map((cat) => {
          const Icon = cat.icon;
          const selected = metricCategory === cat.value;
          const isDisabled = cat.disabled || (isEditMode && !selected);
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => !isDisabled && setMetricCategory(cat.value)}
              disabled={isDisabled}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                selected
                  ? "border-foreground bg-foreground text-background"
                  : isDisabled
                    ? "border-border bg-muted/50 text-muted-foreground cursor-not-allowed"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {cat.label}
              {cat.disabled && !isEditMode && (
                <span className="text-[10px] text-muted-foreground ml-0.5">
                  soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Card 1: Metrics Information */}
      <div className="bg-card rounded-xl shadow-base border border-border p-4">
        <h3 className="text-sm font-semibold text-foreground mb-2">
          Metrics Information
        </h3>
        <p className="text-muted-foreground text-sm mb-6">
          Enter the name, type, and result format for your metric.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <label className="block text-xs font-medium text-foreground mb-2">
              Metrics Name
            </label>
            <input
              type="text"
              value={metricName}
              onChange={(e) => setMetricName(e.target.value)}
              className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              placeholder="Enter metrics name"
            />
          </div>
          {metricCategory !== "code_as_judge" && (
            <div>
              <label className="block text-xs font-medium text-foreground mb-2">
                Evaluation Method
              </label>
              <select
                value={metricType}
                onChange={(e) => setMetricType(e.target.value)}
                className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="">Select a method</option>
                <option value="custom_evaluation">Custom Evaluation</option>
                <option value="specific_text_/_phrase_check">
                  Specific Text/Phrase Check
                </option>
                <option value="permitted_options_check">
                  Permitted Options Check
                </option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-foreground mb-2">
              Result Type
            </label>
            <select
              value={resultType}
              onChange={(e) => {
                setResultType(e.target.value);
                if (e.target.value !== "enum") setEnumValues([]);
              }}
              className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              <option value="boolean">Boolean (Pass / Fail)</option>
              <option value="rating">Rating (1-5)</option>
              {metricType !== "specific_text_/_phrase_check" && (
                <option value="enum">Enum (Custom Values)</option>
              )}
              <option value="numeric">Numeric</option>
            </select>
          </div>
        </div>
      </div>

      {/* Card 2: Configuration */}
      <div className="bg-card rounded-xl shadow-base border border-border p-4">
        <h3 className="text-sm font-semibold text-foreground mb-2">
          Configuration
        </h3>
        <p className="text-muted-foreground text-sm mb-6">
          Configure the evaluation details and LLM instructions for this metric.
        </p>
        <div className="relative min-h-[100px]">
          {metricCategory !== "code_as_judge" && !metricType && (
            <div className="flex flex-col items-center justify-center space-y-2 py-8">
              <p className="text-muted-foreground text-sm font-medium">
                Please select an evaluation method to configure
              </p>
              <p className="text-muted-foreground text-sm">
                Choose from the dropdown above to start configuring your metric
              </p>
            </div>
          )}
          {(metricType || metricCategory === "code_as_judge") && (
            <div className="space-y-6">
              {metricCategory !== "code_as_judge" &&
                renderMetricTypeSpecificFields()}

              {metricCategory === "code_as_judge" ? (
                <CodeMetricEditor
                  code={codeSnippet}
                  onChange={setCodeSnippet}
                  clientId={clientId}
                  agentId={metricAgentId}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">
                    LLM Instructions
                  </label>
                  <p className="text-muted-foreground text-xs mb-2">
                    Provide specific instructions for the LLM on how to evaluate
                    this metric. Use variables to reference conversation data.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {availableVariables.map((v) => (
                      <button
                        key={v.token}
                        type="button"
                        onClick={() => insertVariable(v.token)}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          v.always
                            ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                            : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
                        }`}
                        title={v.hint || v.label}
                      >
                        {v.token}
                        {!v.always && (
                          <span className="ml-1 text-[10px] text-muted-foreground">
                            *
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={llmInstructions}
                    onChange={(e) => setLlmInstructions(e.target.value)}
                    rows={10}
                    className="block w-full min-h-[220px] rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    placeholder={
                      metricType === "custom_evaluation"
                        ? "Describe what the LLM should evaluate. E.g., 'Check if the agent confirmed the customer identity before proceeding...'"
                        : "Provide additional context for the LLM."
                    }
                  />
                </div>
              )}

              {metricCategory !== "code_as_judge" &&
                renderStructuredOutputSection()}
            </div>
          )}
        </div>
      </div>

      {/* Card 3: Trigger State */}
      {metricCategory !== "code_as_judge" && (
        <div className="bg-card rounded-xl shadow-base border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">
            Trigger State
          </h3>
          <p className="text-muted-foreground text-sm mb-6">
            Set when this metric should be evaluated during a call.
          </p>
          <div className="space-y-6">
            <div className="w-1/3">
              <label className="block text-xs font-medium text-foreground mb-2">
                Trigger Type
              </label>
              <select
                value={evaluationTrigger.type}
                onChange={(e) => handleTriggerTypeChange(e.target.value)}
                className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="">Select a trigger type</option>
                <option value="always">Always (Respecting Scope)</option>
                <option value="call_state">If Call Reaches Specific State</option>
              </select>
            </div>
            {evaluationTrigger.type === "call_state" && (
              <div className="w-1/2">
                <label className="block text-xs font-medium text-foreground mb-2">
                  State Prompt
                </label>
                <textarea
                  value={evaluationTrigger.state_prompt}
                  onChange={(e) => handleStatePromptChange(e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Card 4: Scope / Assignment (app only) + Applies-to */}
      <div className="bg-card rounded-xl shadow-base border border-border p-4">
        <div className="space-y-6">
          <div className={resultType === "boolean" ? "grid grid-cols-2 gap-6" : ""}>
            {resultType === "boolean" && (
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isCritical}
                    onClick={() => setIsCritical(!isCritical)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isCritical ? "bg-red-500" : "bg-muted"}`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-card transition-transform ${isCritical ? "translate-x-[18px]" : "translate-x-[3px]"}`}
                    />
                  </button>
                  <div>
                    <span className="text-sm font-medium text-foreground">
                      Critical Metric
                    </span>
                    <p className="text-xs text-muted-foreground">
                      If this metric fails, the entire call is marked as failed
                    </p>
                  </div>
                </label>
              </div>
            )}
            {showAssignment && (
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isGlobal"
                    checked={isGlobal}
                    disabled={isEditMode && isGlobal}
                    onChange={(e) => {
                      setIsGlobal(e.target.checked);
                      if (e.target.checked) setSelectedScenarios([]);
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-input rounded disabled:opacity-50"
                  />
                  <label
                    htmlFor="isGlobal"
                    className="ml-2 block text-sm text-foreground"
                  >
                    Mark as Global
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-1 ml-6">
                  {isEditMode && isGlobal
                    ? "This metric is global and cannot be changed back to agent-specific."
                    : isGlobal
                      ? "Metric will be available across all agents."
                      : "Metric belongs to the selected agent. Choose which scenarios to apply it to below."}
                </p>
              </div>
            )}
          </div>

          {showAssignment && !isGlobal && (
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-foreground mb-2">
                  Agent
                </label>
                <select
                  value={metricAgentId}
                  onChange={(e) => setMetricAgentId(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg shadow-sm text-sm bg-background focus:border-primary focus:ring-primary"
                >
                  <option value="">Select an agent</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>
              {renderScenariosDropdown()}
            </div>
          )}

          {/* Applies-to radios + Observability/Simulation toggles — one row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-medium text-foreground mb-2">
                Applies to
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    value: "both",
                    title: "Text & Voice",
                    hint: "Runs on voice and text.",
                  },
                  {
                    value: "text",
                    title: "Text only",
                    hint: "Skipped on voice runs.",
                  },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-2 rounded-lg border p-2.5 text-sm cursor-pointer transition-colors ${
                      analysisType === opt.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="analysisType"
                      checked={analysisType === opt.value}
                      onChange={() => setAnalysisType(opt.value)}
                      className="mt-0.5 h-4 w-4 shrink-0 text-primary focus:ring-primary border-input"
                    />
                    <span className="min-w-0">
                      <span className="block font-medium text-foreground">
                        {opt.title}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {opt.hint}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground mb-2">
                Metrics enable for
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    on: evalObservability,
                    set: setEvalObservability,
                    title: "Observability",
                    hint: "Measured on production calls.",
                  },
                  {
                    on: evalSimulation,
                    set: setEvalSimulation,
                    title: "Simulation",
                    hint: "Default on test scenarios.",
                  },
                ].map((t) => (
                  <label
                    key={t.title}
                    className="flex items-start gap-2 rounded-lg border border-border p-2.5 text-sm cursor-pointer"
                  >
                    <button
                      type="button"
                      role="switch"
                      aria-checked={t.on}
                      onClick={() => t.set(!t.on)}
                      className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${t.on ? "bg-primary" : "bg-muted"}`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-card transition-transform ${t.on ? "translate-x-[18px]" : "translate-x-[3px]"}`}
                      />
                    </button>
                    <span className="min-w-0">
                      <span className="block font-medium text-foreground">
                        {t.title}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {t.hint}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>

      {!readOnly && (
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input rounded-md hover:bg-muted/50"
          >
            Cancel
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 text-sm font-semibold text-white bg-black border border-transparent rounded-md hover:bg-black/80"
            >
              {submitLabel || (isEditMode ? "Update Metric" : "Create Metric")}
            </button>
          )}
        </div>
      )}
    </fieldset>
  );
};

export default MetricForm;
